// src/pages/Login.js — One login for both internal RGA staff and partners.
// Which app someone lands in is decided by role (users.role), not by which
// URL they signed in from — ProtectedRoute sends a partner to /portal and
// everyone else to / regardless of where this form redirects first, so the
// default here only has to be *a* valid landing page, not the *correct* one.
import { useState } from 'react'
import { useLocation, Navigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const DEFAULT_FROM = '/'

export default function Login() {
  const { signIn, user, loading } = useAuth()
  const location = useLocation()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState(null)
  const [submitting, setSub]    = useState(false)

  // Already authenticated → bounce straight to the dashboard (or origin route).
  if (!loading && user) {
    const from = location.state?.from?.pathname || DEFAULT_FROM
    return <Navigate to={from} replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setSub(true)
    try {
      await signIn(email.trim(), password)
      // Hard navigation, not react-router's navigate(): guarantees a fresh
      // app boot (fresh AuthContext, fresh React Query cache) instead of
      // reusing this page's in-memory state, which could otherwise show a
      // stale cache from whatever was previously loaded in this tab for a
      // moment before the new role's data replaces it. Matches the same
      // hard-navigation pattern api.js uses on 401.
      const from = location.state?.from?.pathname || DEFAULT_FROM
      window.location.assign(from)
    } catch (err) {
      setError(err.message || 'Sign-in failed. Check your email and password.')
      setSub(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'var(--bg)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '380px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
      }}>

        {/* Logo + wordmark */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#3b82f6',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
              RGA CRM
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '4px' }}>
              Rainmaker Global
            </div>
          </div>
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div>
            <h1 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Sign in
            </h1>
            <p style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Sign in with your RGA CRM credentials.
            </p>
          </div>

          {error && (
            <div style={{
              background: 'var(--danger-muted)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 12px',
              fontSize: '13px',
              color: 'var(--danger-text)',
            }}>
              {error}
            </div>
          )}

          <div className="input-group">
            <label className="input-label" htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@rainmakerglobal.com"
              required
              autoFocus
            />
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--accent)', alignSelf: 'flex-end', marginTop: -8 }}>
            Forgot password?
          </Link>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ width: '100%', padding: '10px', marginTop: '4px' }}
          >
            {submitting
              ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Signing in…</>
              : 'Sign In'
            }
          </button>
        </form>

      </div>
    </div>
  )
}
